import { useTranslation } from "react-i18next"
import { ArrowSquareOut, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { PhysicalTherapyIcon } from "@/modules/patients/detail/progress-note/documents/therapy-prescription-document"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { ChPhysiotherapyDrawer } from "@/modules/patients/detail/therapy-prescription/ch/ch-physiotherapy-drawer"
import { chPhysiotherapyFragment$key } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/chPhysiotherapyFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface PhysiotherapyDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  healthcareProviderId: string
  treatmentUnit: number
  physiotherapyRef: chPhysiotherapyFragment$key
}

const PhysiotherapyDocument = ({
  id,
  progressNoteId,
  healthcareProviderId,
  treatmentUnit,
  isEditable = true,
  physiotherapyRef,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: PhysiotherapyDocumentProps) => {
  const { t } = useTranslation()
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  const careProvider = useCareProviderWithOrigin(healthcareProviderId)

  const heathcareProviderName = combineCareProviderTitle({
    businessName: careProvider?.businessName,
    title: careProvider?.title,
    firstName: careProvider?.firstName,
    lastName: careProvider?.lastName,
  })

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="physiotherapy-document-item"
      onClick={() => {
        if (!isEditable && id) {
          onSelect(id)
          return
        }
        openDrawer(
          () => <ChPhysiotherapyDrawer id={id} progressNoteId={progressNoteId} physiotherapyRef={physiotherapyRef} />,
          "PhysiotherapyDrawer",
        )
      }}>
      <PhysicalTherapyIcon isSelected={isSelected} />
      <div className="ml-2 min-w-0">
        <ListText className={cn(isSelected && "text-on-primary-surface")}>
          {t("patient.therapyPrescriptionPhysiotherapy.therapyPrescriptionPhysiotherapy")}
        </ListText>
        <ListText
          data-testid="physiotherapy-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {joinStrings([heathcareProviderName, `${t("patient.therapyPrescription.units")}: ${treatmentUnit}`])}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="physiotherapy-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="physiotherapy-document-button" />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { PhysiotherapyDocument }
